import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function Login() {
  const { auth } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await auth(mode === "login" ? "login" : "register", mode === "login" ? { email: f.email, password: f.password } : f);
      nav(u.onboarded ? "/" : "/onboarding");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="analytics" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <img src="/logo-512.png" alt="NamoAI Digital" className="absolute top-12 left-14 w-44 rounded-2xl shadow-2xl shadow-sky-500/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-[#070B14]/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-14">
          <p className="label-mono mb-4">AI digital marketing agency</p>
          <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-[1.05]">10 AI employees.<br /><span className="text-orange-500">One agency.</span></h1>
          <p className="text-slate-300 mt-5 max-w-md">Strategy, SEO, ads, content, email, web, design and video — run by AI, billed in credits.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 relative z-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="auth-form">
          <div className="flex items-center gap-2 mb-6">
            <img src="/logo-mark.png" alt="NamoAI Digital" className="h-10 w-auto" data-testid="login-logo" />
            <span className="font-display font-bold text-xl">NamoAI <span className="text-orange-500">Digital</span></span>
          </div>
          <h2 className="text-3xl font-bold">{mode === "login" ? "Welcome back" : "Create your agency"}</h2>
          <p className="text-sm text-slate-400 flex items-center gap-2"><Sparkles size={14} className="text-orange-400" /> Explore the dashboard free. Pick a plan when you're ready to work.</p>
          {mode === "signup" && <Input data-testid="auth-name-input" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />}
          <Input data-testid="auth-email-input" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          <Input data-testid="auth-password-input" type="password" placeholder="Password (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
          {err && <p data-testid="auth-error" className="text-sm text-red-400">{err}</p>}
          <Button data-testid="auth-submit-btn" disabled={busy} className="w-full rounded-full h-11">{busy ? "Please wait..." : mode === "login" ? "Log in" : "Sign up"}</Button>
          <div className="flex items-center gap-3 text-xs text-slate-600"><span className="flex-1 h-px bg-slate-800" />OR<span className="flex-1 h-px bg-slate-800" /></div>
          <button type="button" data-testid="google-login-btn" onClick={googleLogin}
            className="w-full h-11 rounded-full border border-slate-700 bg-white text-slate-900 font-medium text-sm flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <button type="button" data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "signup" : "login")} className="text-sm text-slate-400 hover:text-orange-400 w-full">
            {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
          </button>
        </form>
      </div>
    </div>
  );
}
